/**
 * Mobile navigation - replaces the horizontal button row at narrow viewports.
 *
 * Behavior:
 *   - Detects mobile via window.matchMedia (re-evaluates on viewport changes).
 *   - On mobile, shows a single button labeled with the current view.
 *   - Click -> opens a dropdown listing all views.
 *   - Dropdown shows up to 6 items at full height; content beyond scrolls.
 *   - Click outside closes the dropdown.
 *   - Tapping any item switches view AND closes the dropdown.
 *
 * On desktop, the component returns null and the App's normal nav renders.
 *
 * Patch AA: kept the desktop and mobile nav lists in sync. The desktop nav
 * (in App.tsx) had Future Plan removed in patch I and Relocate added in
 * patch W; both changes now propagate here. AppView union also reflects
 * the current set of views, so 'relocate' is a first-class member rather
 * than the `as unknown as AppView` cast App.tsx had been using to dodge
 * this file. Order matches the desktop button row exactly.
 *
 * Patch AM: added 'admin' to the AppView union and the NAV_ITEMS table.
 * Visibility is controlled by a new isAdmin prop (default false) so the
 * tab only renders for admins and the super-admin. Filtering at the prop
 * boundary rather than inside NAV_ITEMS keeps the table a static literal
 * while still respecting the auth-derived gate from App.tsx.
 *
 * Patch AN: added 'myPlan' to the AppView union and NAV_ITEMS. Unlike
 * 'admin' this one is visible to every signed-in user (the page itself
 * shows an empty state if no plans have been uploaded to them yet), so
 * no filtering. Placed before 'admin' so the rightmost tab is still the
 * privileged one.
 */

import { useEffect, useRef, useState } from 'react';
import { getCurrentSubdomain } from '../io/tenant.js';

export type AppView =
  | 'dashboard'
  | 'purchases'
  | 'security'
  | 'salaryPlan'
  | 'loopLoan'
  | 'carry'
  | 'powerlaw'
  | 'relocate'
  | 'news'
  | 'assumptions'
  | 'prism'
  | 'myPlan'
  | 'admin';

const NAV_ITEMS: Array<{ id: AppView; label: string }> = [
  { id: 'dashboard',   label: 'Dashboard' },
  { id: 'purchases',   label: 'Positions' },
  { id: 'salaryPlan',  label: 'Career' },
  { id: 'loopLoan',    label: 'Leverage' },
  { id: 'carry',       label: 'Retire' },
  // Patch BR: Power Law removed from the nav; content folded into
  // Assumptions. The 'powerlaw' AppView value is still in the type so
  // App.tsx can migrate persisted state.
  { id: 'relocate',    label: 'Relocate' },
  { id: 'security',    label: 'Security' },
  { id: 'news',        label: 'News' },
  { id: 'assumptions', label: 'Assumptions' },
  // PRISM: paul-only (stackbtc.net personal instance). Filtered out of the
  // dropdown for every other tenant in the baseItems gate below.
  { id: 'prism',       label: 'PRISM' },
  { id: 'myPlan',      label: 'My Plan' },
  { id: 'admin',       label: 'Admin' },
];

const MOBILE_BREAKPOINT_PX = 720;
const ITEM_HEIGHT_PX = 44;
const VISIBLE_ITEMS = 6;

export function useIsMobile(breakpointPx: number = MOBILE_BREAKPOINT_PX): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    if (typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(`(max-width: ${breakpointPx}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(`(max-width: ${breakpointPx}px)`);
    const handler = (e: MediaQueryListEvent): void => setIsMobile(e.matches);
    // Modern API
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    return undefined;
  }, [breakpointPx]);

  return isMobile;
}

interface MobileNavProps {
  view: AppView;
  setView: (v: AppView) => void;
  /** Patch AM: when false (the default), the Admin item is filtered out
   *  of the dropdown. Mirrors the desktop nav's conditional render in
   *  App.tsx. */
  isAdmin?: boolean;
  /** Per-tenant label overrides. Keys are AppView ids; values replace the
   *  default NAV_ITEMS label. Used to rename e.g. "Career"→"Income" and
   *  "Retire"→"FIRE" on bradlong without duplicating the entire items table. */
  labelOverrides?: Partial<Record<AppView, string>>;
}

export function MobileNav({ view, setView, isAdmin = false, labelOverrides = {} }: MobileNavProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Filter the nav items by gate: Admin only for admins (Patch AM), PRISM
  // only for the paul tenant (stackbtc.net personal instance). Done here
  // rather than at module scope so the same NAV_ITEMS literal serves every
  // tenant; the gates are evaluated per render against the live auth/host.
  const isPaul = getCurrentSubdomain() === 'paul';
  const baseItems = NAV_ITEMS.filter(i => {
    if (i.id === 'admin') return isAdmin;
    if (i.id === 'prism') return isPaul;
    return true;
  });
  const items = baseItems.map(i => labelOverrides[i.id] ? { ...i, label: labelOverrides[i.id]! } : i);
  const current = items.find(i => i.id === view) ?? items[0]!;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent | TouchEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '8px 12px',
          fontSize: 'var(--fs-small)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--bg-elev)',
          border: '1px solid var(--rule)',
          borderRadius: 4,
          color: 'var(--text)',
          cursor: 'pointer',
        }}
      >
        <span>{current.label}</span>
        <span style={{ fontSize: 'var(--fs-micro)', color: 'var(--text-faint)', marginLeft: 8 }}>
          {open ? '\u25B2' : '\u25BC'}
        </span>
      </button>

      {open ? (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            maxHeight: ITEM_HEIGHT_PX * VISIBLE_ITEMS,
            overflowY: 'auto',
            background: 'var(--bg-elev)',
            border: '1px solid var(--rule-strong)',
            borderRadius: 4,
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            zIndex: 100,
          }}
        >
          {items.map(item => {
            const isActive = item.id === view;
            return (
              <button
                key={item.id}
                role="option"
                aria-selected={isActive}
                onClick={() => {
                  setView(item.id);
                  setOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '0 12px',
                  height: ITEM_HEIGHT_PX,
                  lineHeight: `${ITEM_HEIGHT_PX}px`,
                  fontSize: 'var(--fs-small)',
                  background: isActive ? 'var(--rule)' : 'transparent',
                  color: isActive ? 'var(--text)' : 'var(--text-soft)',
                  border: 'none',
                  borderBottom: '1px solid var(--rule)',
                  cursor: 'pointer',
                }}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
